'use client';
import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';

import { useTranslation } from '@/app/hooks/useTranslation';

import { useAppReleaseGate } from './useAppReleaseGate';

// 업데이트가 필요한 앱에 안내를 띄운다.
//
// blocked 면 화면 전체를 덮어 아무것도 못 하게 한다. 이 앱은 WebView 셸이라 이것이
// 곧 강제 업데이트다 — 네이티브를 고치지 않고 서버 설정만으로 걸 수 있다.
// suggested 면 닫을 수 있는 안내만 띄운다.
//
// 기준값이 0 이면(기본) 아무것도 뜨지 않는다. 버전을 실어 보내는 빌드가 스토어에
// 보급된 뒤에 서버에서 올린다 — 그전에 올리면 토큰 없는 구버전이 전부 막힌다.
const AppReleaseGate = () => {
    const { blocked, suggested, storeUrl } = useAppReleaseGate();
    const [dismissed, setDismissed] = useState(false);
    // 기기(OS) 언어 또는 사용자가 고른 언어로 보여 준다 — 저장된 선택이 없으면 기기 언어를 따른다.
    const { t } = useTranslation();

    if (!blocked && (!suggested || dismissed)) return null;

    const goStore = () => {
        if (storeUrl) window.location.href = storeUrl;
    };

    // 권고(suggested)는 막지 않는다 — 하단 배너로 띄우고 닫을 수 있게 둔다.
    //
    // 처음엔 blocked 와 같은 전면 패널을 썼는데, 그러면 "닫을 수 있는 안내" 라고 적어 둔
    // 주석·설정 문구와 실제 동작이 갈린다. 설정값을 올리는 운영자는 배너를 기대하는데
    // 화면이 통째로 덮이면 그것은 사실상 강제다(2026-09-19 확인).
    if (!blocked) {
        return (
            <Box
                data-gate="app-release"
                sx={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2147483000,
                    bgcolor: '#111111',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Typography variant="h2_12Medium" sx={{ flex: 1, color: '#ffffff' }}>
                    {t('gate_banner_text', '새 버전이 나왔어요')}
                </Typography>
                {storeUrl && (
                    <Button variant="text" onClick={goStore} sx={{ color: '#7BA7FF', minWidth: 0 }}>
                        {t('gate_banner_update', '업데이트')}
                    </Button>
                )}
                <Button variant="text" onClick={() => setDismissed(true)} sx={{ color: '#999999', minWidth: 0 }}>
                    {t('gate_banner_close', '닫기')}
                </Button>
            </Box>
        );
    }

    return (
        <Box
            data-gate="app-release"
            sx={{
                position: 'fixed',
                inset: 0,
                // 무엇보다 위에 있어야 한다. 막는 화면이 다른 것에 가리면 강제가 아니라
                // 그냥 DOM 에만 있는 안내가 된다 — 실제로 2000 으로 뒀다가 온보딩
                // 화면에 덮였다(2026-09-18 stage 실측). 이 레포는 999999 까지 쓴다.
                zIndex: 2147483000,
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4,
                textAlign: 'center',
                gap: 2,
            }}
        >
            <Typography variant="h2_18Bold" sx={{ color: '#111111' }}>
                {t('gate_block_title', '앱 업데이트가 필요해요')}
            </Typography>

            <Typography variant="h2_14Regular" sx={{ color: '#666666', whiteSpace: 'pre-line' }}>
                {t('gate_block_body', '지금 쓰는 앱 버전은 더 이상 지원되지 않아요.\n스토어에서 최신 버전으로 업데이트한 뒤 다시 열어 주세요.')}
            </Typography>

            <Typography variant="h2_12Medium" sx={{ color: '#888888', whiteSpace: 'pre-line' }}>
                {t('gate_block_note', '계정·포인트·작품은 그대로예요.\n마이룸 같은 새 기능은 최신 앱에서만 쓸 수 있어요.')}
            </Typography>

            {storeUrl ? (
                <Button variant="contained" onClick={goStore} sx={{ borderRadius: '20px', mt: 1, px: 4 }}>
                    {t('gate_block_button', '업데이트하러 가기')}
                </Button>
            ) : (
                // 스토어 주소를 아직 못 넣은 플랫폼. 버튼 대신 방법을 말해 준다 —
                // 링크를 지어내 엉뚱한 앱으로 보내는 것보다 낫다.
                <Typography variant="h2_12Medium" sx={{ color: '#999999', mt: 1 }}>
                    {t('gate_block_no_store', '앱스토어에서 ‘조이쌤 커넥트’를 검색해 업데이트해 주세요.')}
                </Typography>
            )}

            {/* 어린이 회원이 업데이트를 못 하는 경우(부모 승인, 저장공간, 오래된 기기)를 위한 안내 */}
            <Typography variant="h2_12Regular" sx={{ color: '#999999', whiteSpace: 'pre-line', mt: 1 }}>
                {t('gate_block_help', '업데이트가 안 되면 부모님께 도움을 요청해 주세요.\n기기 저장공간이 부족하거나 기기가 너무 오래된 경우에도 업데이트가 안 될 수 있어요.')}
            </Typography>
        </Box>
    );
};

export default AppReleaseGate;
